import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Button, Input, Table } from 'reactstrap';

export default function Notifications({ role = 'admin' }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL ;
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');

  const load = async (p = page, l = limit) => {
    try {
      setLoading(true);
      const r = role?.toLowerCase();
      const basePath = r === 'superadmin' ? 'superadmin' : r === 'orguser' ? 'orguser' : 'admin';
      const res = await fetch(`${API_BASE}/${basePath}/notifications?page=${p}&limit=${l}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) throw new Error('Failed to load notifications');
      const data = await res.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      setItems(list.map(n => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        isRead: n.isRead,
        createdAt: n.createdAt,
        organizationName: n.organization?.organizationName,
      })));
      setTotal(data?.pagination?.total || list.length);
      setPage(data?.pagination?.page || p);
      setLimit(data?.pagination?.limit || l);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      const r = role?.toLowerCase();
      const basePath = r === 'superadmin' ? 'superadmin' : r === 'orguser' ? 'orguser' : 'admin';
      const res = await fetch(`${API_BASE}/${basePath}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) throw new Error('Failed to mark notification as read');
      setItems(prev => prev.map(i => i.id === id ? { ...i, isRead: true } : i));
      // Trigger storage event to update Navbar notification count
      window.dispatchEvent(new Event('storage'));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { load(1, limit); /* eslint-disable-next-line */ }, [role]);

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="notifications-page">
      <Row className="mb-3">
        <Col>
          <h2>Notifications</h2>
          <p className="text-muted">View and manage system notifications</p>
        </Col>
        <Col md="3" className="text-end">
          <Input type="select" value={limit} onChange={(e) => { const l = Number(e.target.value); setLimit(l); load(1, l); }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </Input>
        </Col>
      </Row>

      <Card>
        <CardBody>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Type</th>
                    {role?.toLowerCase() === 'superadmin' && <th>Organization</th>}
                    <th>Created</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={role?.toLowerCase() === 'superadmin' ? 7 : 6} className="text-center">No notifications</td></tr>
                  ) : items.map(n => (
                    <tr key={n.id}>
                      <td>{n.title}</td>
                      <td>{n.message}</td>
                      <td>{n.type}</td>
                      {role?.toLowerCase() === 'superadmin' && <td>{n.organizationName || '—'}</td>}
                      <td>{n.createdAt ? new Date(n.createdAt).toLocaleString() : '—'}</td>
                      <td>{n.isRead ? 'Read' : 'Unread'}</td>
                      <td>
                        {!n.isRead && (
                          <Button size="sm" color="primary" onClick={() => markRead(n.id)}>Mark as read</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <span>Total: {total}</span>
            <div className="d-flex align-items-center gap-2">
              <Button disabled={page <= 1} onClick={() => { const p = Math.max(1, page - 1); setPage(p); load(p, limit); }}>Prev</Button>
              <span>Page {page} of {pages}</span>
              <Button disabled={page >= pages} onClick={() => { const p = Math.min(pages, page + 1); setPage(p); load(p, limit); }}>Next</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
